import { apiRequestExternal } from "./apiRequestExternal";
import { composerBrowserBaseUrl } from "./controllerRegistryAdmin";

/**
 * Admin session hand-off (seamless SSO from the public origin to the IAP-protected
 * admin origin). See composer/app/security/admin_handoff.py for the server contract.
 *
 * Flow:
 *   1. On the public host, an authenticated admin calls {@link createAdminHandoff}
 *      which sets a single-use, parent-domain HttpOnly cookie.
 *   2. The browser navigates to the admin host (via the /admin middleware redirect).
 *   3. On the admin host, {@link redeemAdminHandoff} exchanges the cookie for a
 *      Firebase custom token so the admin origin establishes its own session.
 */

const CREATE_HANDOFF_PATH =
  "/v1/public/regional/auth/user/user_session/admin-handoff";
const REDEEM_HANDOFF_PATH = "/v1/private/admin/session/redeem-handoff";

/**
 * Mint an admin hand-off cookie for the currently signed-in admin.
 *
 * @param {string} accessToken - Live Firebase ID token of the admin user.
 * @returns {Promise<{ data: unknown, status: number }>}
 */
export async function createAdminHandoff(accessToken) {
  if (!accessToken) {
    throw new Error("Missing access token for admin hand-off.");
  }
  const base = composerBrowserBaseUrl().replace(/\/$/, "");
  // Body is an empty object, not null: the Firebase functions-framework wraps the
  // Next app and strict-parses JSON bodies before our proxy route runs. Strict mode
  // rejects top-level primitives (a stringified `null`) with a 400, so we send `{}`.
  // includeCredentials so the browser stores the Set-Cookie hand-off cookie.
  return apiRequestExternal(
    `${base}${CREATE_HANDOFF_PATH}`,
    "POST",
    {},
    { Authorization: accessToken },
    true
  );
}

/**
 * Redeem the admin hand-off cookie (admin origin) for a Firebase custom token.
 *
 * @returns {Promise<{ data: { access_token?: string } | null, status: number }>}
 */
export async function redeemAdminHandoff() {
  const base = composerBrowserBaseUrl().replace(/\/$/, "");
  // Empty-object body (not null): the functions-framework strict JSON body-parser
  // rejects a stringified `null` with a 400 before the request reaches our proxy.
  // includeCredentials so the browser sends the hand-off cookie to the proxy.
  return apiRequestExternal(
    `${base}${REDEEM_HANDOFF_PATH}`,
    "POST",
    {},
    {},
    true
  );
}
